// src/components/quiz/TimeAttackMode.js
// Modalità "Time Attack": 15 domande fisse, 90 secondi totali condivisi.
// Sbagliare non finisce la partita (niente game over): si prosegue sempre
// verso la domanda successiva. Punteggio fisso per risposta corretta (niente
// timer per-domanda, quindi niente bonus legato al tempo rimasto sulla
// singola domanda) + bonus velocità se si finiscono tutte le domande in
// anticipo sul tempo totale.
import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Timer, CheckCircle2, XCircle } from 'lucide-react';
import { fmtTime, OptionBtn, LETTERS } from './quizShared';
import { pickNextQuestion } from './quizGenerators';

export const TOTAL_QUESTIONS = 15;
export const TOTAL_SECONDS = 90;
const FEEDBACK_DELAY = 600;
const SPEED_BONUS_PER_SECOND = 10;

function buildQuestionSet(players) {
  const list = [];
  const recentTexts = [];
  for (let i = 0; i < TOTAL_QUESTIONS; i++) {
    const q = pickNextQuestion(players, recentTexts);
    if (!q) break;
    list.push(q);
    recentTexts.unshift(q.question);
    // Vedi lo stesso commento in SurvivalMode.js: finestra più corta del
    // totale di template disponibili (18), altrimenti su una partita da
    // 15 domande il filtro anti-ripetizione smette presto di avere effetto.
    if (recentTexts.length > 6) recentTexts.length = 6;
  }
  return list;
}

export default function TimeAttackMode({ players, onFinish }) {
  const [questions] = useState(() => buildQuestionSet(players));
  const [idx,        setIdx]        = useState(0);
  const [timeLeft,   setTimeLeft]   = useState(TOTAL_SECONDS);
  const [selected,   setSelected]   = useState(null);
  const [breakdown,  setBreakdown]  = useState([]);
  const [totalScore, setTotalScore] = useState(0);

  const timerRef    = useRef(null);
  const finishedRef = useRef(false);
  const stateRef    = useRef({ breakdown: [], totalScore: 0, idx: 0 });

  // Tiene sempre disponibile lo stato più recente dentro l'interval del
  // timer globale, che altrimenti catturerebbe closure ormai stale.
  useEffect(() => {
    stateRef.current = { breakdown, totalScore, idx };
  }, [breakdown, totalScore, idx]);

  // Nota: non legge `timeLeft` dalla closure (l'interval qui sotto è montato
  // una sola volta e chiuderebbe su un `finish` con `timeLeft` sempre allo
  // stato iniziale) — ogni chiamante passa `totalSecs` esplicitamente.
  const finish = (payload) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearInterval(timerRef.current);
    onFinish({ mode: 'timeattack', ...payload });
  };

  // Timer globale unico: nessun timer per-domanda.
  useEffect(() => {
    if (!questions.length) return undefined;

    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          // Tempo scaduto: le domande non raggiunte vengono segnate come tali.
          const { breakdown: bd, totalScore: score } = stateRef.current;
          const reachedCount = bd.length;
          const missing = questions.slice(reachedCount).map(q => ({
            question: q.question,
            reached: false,
          }));
          finish({ breakdown: [...bd, ...missing], totalScore: score, totalSecs: TOTAL_SECONDS });
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  const q = questions[idx];

  const handleSelect = (optIdx) => {
    if (!q || selected !== null || finishedRef.current) return;
    setSelected(optIdx);

    const isCorrect = q.correctIndices.includes(optIdx);
    const pts = isCorrect ? Math.round(100 * (q.scoreMultiplier || 1)) : 0;

    const entry = {
      question: q.question,
      options: q.options,
      correctIndices: q.correctIndices,
      selectedIndex: optIdx,
      correct: isCorrect,
      score: pts,
      explanation: q.explanation,
      reached: true,
    };

    window.setTimeout(() => {
      if (finishedRef.current) return;
      const newBreakdown = [...stateRef.current.breakdown, entry];
      const newTotalScore = stateRef.current.totalScore + pts;

      if (idx + 1 >= questions.length) {
        // Tutte le domande risposte prima dello scadere del tempo: bonus velocità.
        const bonus = SPEED_BONUS_PER_SECOND * timeLeft;
        finish({
          breakdown: newBreakdown,
          totalScore: newTotalScore + bonus,
          totalSecs: TOTAL_SECONDS - timeLeft,
          speedBonus: bonus,
        });
        return;
      }

      setBreakdown(newBreakdown);
      setTotalScore(newTotalScore);
      setIdx(i => i + 1);
      setSelected(null);
    }, FEEDBACK_DELAY);
  };

  const optionState = (optIdx) => {
    if (selected === null) return null;
    if (q.correctIndices.includes(optIdx)) return 'correct';
    if (optIdx === selected) return 'wrong';
    return 'dimmed';
  };

  const isAnswered = selected !== null;
  const isCorrect  = isAnswered && q.correctIndices.includes(selected);
  const timerWarn  = timeLeft <= 15;

  if (!q) {
    return (
      <div className="quiz-panel glass" style={{ textAlign: 'center', padding: 32 }}>
        <p style={{ color: 'var(--muted)' }}>Impossibile generare le domande per Time Attack.</p>
      </div>
    );
  }

  return (
    <div className="quiz-game quiz-game--timeattack">
      <div className="quiz-game__top">
        <div className="quiz-game__progress">
          <span>Domanda {idx + 1} / {questions.length}</span>
          <div className="quiz-progress-bar">
            <div className="quiz-progress-fill" style={{ width: `${((idx) / questions.length) * 100}%` }} />
          </div>
        </div>
        <div className="quiz-game__stats">
          <div className="quiz-game__stat">
            <Trophy size={13} />
            <strong>{totalScore} pt</strong>
          </div>
          <div className={`quiz-game__stat quiz-game__stat--timer ${timerWarn ? 'quiz-game__stat--warn' : ''}`}>
            <Timer size={13} />
            <strong>{fmtTime(timeLeft)}</strong>
          </div>
        </div>
      </div>

      <div className={`quiz-timer-bar ${timerWarn ? 'quiz-timer-bar--warn' : ''}`}>
        <div className="quiz-timer-bar__fill" style={{ width: `${(timeLeft / TOTAL_SECONDS) * 100}%` }} />
      </div>

      <div className="quiz-question glass">
        <p className="quiz-question__num">Domanda {idx + 1}</p>
        <h2 className="quiz-question__text">{q.question}</h2>

        {isAnswered && (
          <div className={`quiz-question__feedback ${isCorrect ? 'quiz-question__feedback--ok' : 'quiz-question__feedback--err'}`}>
            {isCorrect ? (
              <><CheckCircle2 size={15} /> Corretto! — {q.explanation}</>
            ) : (
              <><XCircle size={15} /> Sbagliato — {q.explanation}</>
            )}
          </div>
        )}
      </div>

      <div className="quiz-options">
        {q.options.map((opt, i) => (
          <OptionBtn
            key={i}
            option={opt}
            letter={LETTERS[i]}
            state={optionState(i)}
            onClick={() => handleSelect(i)}
          />
        ))}
      </div>
    </div>
  );
}
